import { RGBFormat, SRGBColorSpace, NoToneMapping, LinearFilter, OrthographicCamera, Scene, WebGLRenderer, ShaderMaterial, PlaneGeometry, Mesh } from 'three'
import Experience from '../Experience.js'
import fragmentShader from "../../shaders/background/fragment.glsl";
import vertexShader from "../../shaders/background/vertex.glsl";
export default class Background {
    constructor(container) {
        this.container = container;
        this.experience = new Experience();
        this.ui = this.experience.ui;
        this.sizes = this.experience.sizes;
        this.resources = this.experience.resources

        this.progress = 0;
        this.totalDuration = 0;
        this.videoLoopCount = 3;
        this.init();
        this.initEvents();
    }

    initTexture() {
        if(this.resources.items.videoTextureBackground !== null) {

            this.videoTexture = this.resources.items.videoTextureBackground
            this.videoTexture.videoTexture.format = RGBFormat;
            this.videoTexture.videoTexture.colorSpace = SRGBColorSpace;
            this.videoTexture.videoTexture.minFilter = LinearFilter
            this.videoTexture.videoTexture.magFilter = LinearFilter
            this.videoTexture.videoTexture.generateMipmaps = false
            this.videoTexture.videoTexture.flipY = false
            this.videoElement = this.videoTexture.videoElement

            this.uniforms.uTexture.value = this.videoTexture.videoTexture;
            this.totalDuration = this.videoElement.duration * this.videoLoopCount;
        } else {
            this.videoTexture = null;
        }
    }

    init() {
        this.scene = new Scene();

        this.resources.on('ready', () => {
            this.initTexture()
        });

        // Create an orthographic camera
        this.camera = new OrthographicCamera(
            -1, 1,
            1,  -1,
            -1, 1
        );

        // Create the renderer and add it to the document
        this.renderer = new WebGLRenderer({
            powerPreference: "high-performance",
            antialias: false,
            alpha: false,
            stencil: false,
            depth: false,
            useLegacyLights: false,
            physicallyCorrectLights: false,
        });

        this.clearColor = '#8272FB'
        this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        this.renderer.setSize(window.innerWidth, window.innerHeight * 1.2);
        this.container.appendChild(this.renderer.domElement);

        this.renderer.outputColorSpace = SRGBColorSpace
        this.renderer.toneMapping = NoToneMapping
        this.renderer.setClearColor( this.clearColor, 1 )


        // Create uniforms
        this.uniforms = {
            uTexture: { value: null },
            uScrollRatio: { value: 0 },
            uTotalTextureHeight: { value: 1 },
        };

        // Create the shader material
        this.material = new ShaderMaterial({
            uniforms: this.uniforms,
            vertexShader: vertexShader,
            fragmentShader: fragmentShader,
        });

        // Create the plane geometry and mesh
        this.geometry = new PlaneGeometry(2, 2);
        this.mesh = new Mesh(this.geometry, this.material);
        this.scene.add(this.mesh);
    }

    updateUniforms() {
        const winHeight = window.innerHeight;
        const docHeight = Math.max(
            document.body.scrollHeight, document.documentElement.scrollHeight,
            document.body.offsetHeight, document.documentElement.offsetHeight,
            document.body.clientHeight, document.documentElement.clientHeight
        );

        const totalTextureHeight = docHeight / winHeight;

        // Use this.progress instead of scroll calculations
        this.uniforms.uScrollRatio.value = this.progress;
        this.uniforms.uTotalTextureHeight.value = totalTextureHeight;
    }

    onWindowResize() {
        this.renderer.setSize(window.innerWidth, window.innerHeight * 1.2);
        this.updateUniforms();
    }

    render() {
        this.updateUniforms();
        this.renderer.render(this.scene, this.camera);
    }

    initEvents() {
        window.addEventListener('resize', this.onWindowResize.bind(this));
        this.ui.on('backgroundUpdate', this.update.bind(this));
    }

    update(progress) {
        if (this.totalDuration === 0) return;

        this.progress = progress;
        const currentLoopTime = (progress * this.totalDuration) % this.videoElement.duration;
        if (Math.abs(this.videoElement.currentTime - currentLoopTime) > 0.01) {
            this.videoElement.currentTime = currentLoopTime;
            this.videoTexture.videoTexture.needsUpdate = true;
        }
    }

    destroy() {
        window.removeEventListener('resize', this.onWindowResize.bind(this));
        this.ui.off('backgroundUpdate', this.update.bind(this));

        // Dispose of Three.js objects
        if (this.geometry) {
            this.geometry.dispose();
        }
        if (this.material) {
            this.material.dispose();
        }
        if (this.mesh) {
            this.scene.remove(this.mesh);
        }
        if (this.renderer) {
            this.renderer.dispose();
        }

        // Remove the renderer from the DOM
        if (this.container && this.renderer) {
            this.container.removeChild(this.renderer.domElement);
        }

        // Clean up video texture
        if (this.videoTexture) {
            this.videoTexture.videoTexture.dispose();
        }
        if (this.videoElement) {
            this.videoElement.pause();
            this.videoElement.src = '';
            this.videoElement.load();
        }

        this.scene = null;
        this.camera = null;
        this.renderer = null;
        this.geometry = null;
        this.material = null;
        this.mesh = null;
        this.videoTexture = null;
        this.videoElement = null;
        this.uniforms = null;

        if (this.experience && this.experience.background === this) {
            this.experience.background = null;
        }

        this.container = null;
        this.experience = null;
        this.ui = null;
        this.sizes = null;
        this.resources = null;
    }
}
